import type {
  LocationCountry,
  LocationProvince,
} from '@gokrew/models/location';
import api from './client';

export interface CrmContact {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
}
export interface CrmPartner {
  id: string;
  name: string;
  kind: 'broker' | 'hr_consultant';
  email: string | null;
}
export interface CrmCompanyInput {
  name: string;
  companySize: string;
  estimatedEmployees: number | null;
  source: string;
  partnerId: string | null;
  ownerUserId?: string | null;
  country: string;
  city: string;
  province: string;
  product: string;
  atsMrrCents: number | null;
  stage: string;
  notes: string;
  contacts: CrmContact[];
  version?: number;
}
export interface CrmCompany extends CrmCompanyInput {
  id: string;
  partner: CrmPartner | null;
  owner?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  version: number;
  mrrCents: number | null;
  arrCents: number | null;
  currency: string;
}
const endpoints = {
  companies: '/admin/crm/companies',
  partners: '/admin/crm/partners',
};
export async function getCrmCompanies(search: string, page: number) {
  const { data } = await api.get<{
    items: CrmCompany[];
    total: number;
    page: number;
    pageSize: number;
  }>(endpoints.companies, { params: { search, page } });
  return data;
}
export async function saveCrmCompany(payload: CrmCompanyInput, id?: string) {
  const { data } = id
    ? await api.put<CrmCompany>(`${endpoints.companies}/${id}`, payload)
    : await api.post<CrmCompany>(endpoints.companies, payload);
  return data;
}
export async function deleteCrmCompany(company: CrmCompany) {
  await api.delete(`${endpoints.companies}/${company.id}`, {
    params: { version: company.version },
  });
}
export async function getCrmPartners() {
  const { data } = await api.get<CrmPartner[]>(endpoints.partners);
  return data;
}
export async function addCrmPartner(payload: {
  name: string;
  kind: string;
  email?: string;
}) {
  const { data } = await api.post<CrmPartner>(endpoints.partners, payload);
  return data;
}

export type CrmDealStage = 'qualified' | 'demo' | 'trial' | 'won' | 'lost';
export type CrmDealsVisibility = 'team' | 'private';
export interface CrmMember {
  userId: string;
  dealsVisibility: CrmDealsVisibility;
  /** The Leads section is hidden for this member. */
  leadsHidden: boolean;
  email: string;
  firstName: string;
  lastName: string;
}
export interface CrmPersonInput {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  companyId?: string | null;
  version?: number;
}
export interface CrmPerson extends CrmPersonInput {
  id: string;
  /** Full name, kept by the server from first and last name. */
  name: string;
  company: { id: string; name: string } | null;
  version: number;
}
export interface CrmDealInput {
  name: string;
  companyId: string;
  contactId?: string | null;
  ownerUserId: string;
  stage: CrmDealStage;
  product: 'hris' | 'ats';
  estimatedEmployees?: number | null;
  atsMrrCents?: number | null;
  expectedCloseDate?: string | null;
  notes?: string;
  version?: number;
}
export interface CrmDeal extends CrmDealInput {
  id: string;
  version: number;
  company: CrmCompany;
  contact: CrmPerson | null;
  owner: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  mrrCents: number | null;
  arrCents: number | null;
  currency: string;
  createdAt: string;
  updatedAt: string;
}
export interface CrmActivity {
  id: string;
  dealId: string | null;
  deal?: { id: string; name: string } | null;
  contactId: string | null;
  actorUserId: string | null;
  actor: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  kind: string;
  title: string;
  body: string | null;
  dueAt: string | null;
  completedAt: string | null;
  metadata: Record<string, unknown> | null;
  occurredAt: string;
  createdAt: string;
  /** AI one-liner of a synced email ("JP demande à Maryse..."). */
  summary?: string | null;
}
export interface CrmActivityInput {
  kind: 'note' | 'call' | 'meeting' | 'task';
  title: string;
  body?: string;
  dueAt?: string | null;
  completedAt?: string | null;
  occurredAt?: string | null;
}
export interface CrmPageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
export interface CrmDashboard {
  totals: {
    openDeals: number;
    openMrrCents: number;
    openArrCents: number;
    weightedArrCents: number;
    wonDeals: number;
    wonMrrCents: number;
    wonArrCents: number;
    companies: number;
    contacts: number;
  };
  stages: {
    stage: CrmDealStage;
    count: number;
    mrrCents: number;
    arrCents: number;
  }[];
  recentActivities: CrmActivity[];
  dueActivities: CrmActivity[];
}
export type CrmInboxProvider = 'google' | 'microsoft';
export interface CrmInbox {
  provider: CrmInboxProvider;
  configured: boolean;
  status: 'not_connected' | 'connected' | 'needs_reconnect';
  email: string | null;
  lastSyncedAt: string | null;
  error: string | null;
  /** Meetings are read from this account's calendar. */
  calendar: boolean;
  calendarSyncedAt: string | null;
}
const workspaceEndpoints = {
  dashboard: '/admin/crm/dashboard',
  attention: '/admin/crm/attention',
  members: '/admin/crm/members',
  deals: '/admin/crm/deals',
  contacts: '/admin/crm/contacts',
  activities: '/admin/crm/activities',
  inboxes: '/admin/crm/inboxes',
};
export type CrmAttentionKind =
  | 'support_request'
  | 'email_unanswered'
  | 'task_overdue'
  | 'close_passed'
  | 'meeting_followup'
  | 'stalled'
  | 'close_soon'
  | 'no_next_step'
  | 'task_due'
  | 'leads_waiting';
/** One line of the dashboard's Needs your attention list. */
export interface CrmAttentionItem {
  key: string;
  kind: CrmAttentionKind;
  /** Sales to-dos for prospects, or questions from real customers. */
  category: 'sales' | 'support';
  severity: 'danger' | 'warning' | 'info';
  dealId: string | null;
  dealName: string | null;
  companyName: string | null;
  ownerUserId: string | null;
  stage: CrmDealStage | null;
  activityId: string | null;
  days: number | null;
  at: string | null;
  contactName: string | null;
  subject: string | null;
  summary: string | null;
  replyTo: string | null;
  count: number | null;
}
export async function getCrmAttention() {
  const { data } = await api.get<{ items: CrmAttentionItem[] }>(
    workspaceEndpoints.attention
  );
  return data.items;
}
/** "Handled" on a Support line: that customer's open emails are done. */
export async function handleCrmSupport(id: string) {
  await api.post(`${workspaceEndpoints.attention}/handle`, { id });
}
export async function snoozeCrmAttention(key: string, days: 1 | 7) {
  await api.post(`${workspaceEndpoints.attention}/snooze`, { key, days });
}
export async function getCrmDashboard() {
  const { data } = await api.get<CrmDashboard>(workspaceEndpoints.dashboard);
  return data;
}
export async function getCrmMembers() {
  const { data } = await api.get<CrmMember[]>(workspaceEndpoints.members);
  return data;
}
export async function updateCrmProfile(payload: {
  firstName: string;
  lastName: string;
  language: 'en' | 'fr';
}) {
  const { data } = await api.put('/admin/crm/profile', payload);
  return data;
}
export async function setCrmDealsVisibility(
  dealsVisibility: CrmDealsVisibility
) {
  const { data } = await api.put('/admin/crm/profile/deals-visibility', {
    dealsVisibility,
  });
  return data;
}
export async function inviteCrmMember(payload: {
  email: string;
  firstName: string;
  lastName: string;
}) {
  const { data } = await api.post(workspaceEndpoints.members, payload);
  return data;
}
export async function removeCrmMember(userId: string) {
  await api.delete(`${workspaceEndpoints.members}/${userId}`);
}
export async function getCrmContacts(
  params: { search?: string; companyId?: string; page?: number } = {}
) {
  const { data } = await api.get<CrmPageResult<CrmPerson>>(
    workspaceEndpoints.contacts,
    { params }
  );
  return data;
}
export async function saveCrmContact(payload: CrmPersonInput, id?: string) {
  const { data } = id
    ? await api.put<CrmPerson>(`${workspaceEndpoints.contacts}/${id}`, payload)
    : await api.post<CrmPerson>(workspaceEndpoints.contacts, payload);
  return data;
}
export async function deleteCrmContact(contact: CrmPerson) {
  await api.delete(`${workspaceEndpoints.contacts}/${contact.id}`, {
    params: { version: contact.version },
  });
}
export async function getCrmDeals(
  params: {
    search?: string;
    companyId?: string;
    ownerUserId?: string;
    stage?: string;
    page?: number;
    pageSize?: number;
  } = {}
) {
  const { data } = await api.get<CrmPageResult<CrmDeal>>(
    workspaceEndpoints.deals,
    { params }
  );
  return data;
}
export async function getCrmDeal(id: string) {
  const { data } = await api.get<CrmDeal>(`${workspaceEndpoints.deals}/${id}`);
  return data;
}
export async function saveCrmDeal(payload: CrmDealInput, id?: string) {
  const { data } = id
    ? await api.put<CrmDeal>(`${workspaceEndpoints.deals}/${id}`, payload)
    : await api.post<CrmDeal>(workspaceEndpoints.deals, payload);
  return data;
}
export async function deleteCrmDeal(deal: CrmDeal) {
  await api.delete(`${workspaceEndpoints.deals}/${deal.id}`, {
    params: { version: deal.version },
  });
}
export async function moveCrmDeal(deal: CrmDeal, stage: CrmDealStage) {
  const { data } = await api.put<CrmDeal>(
    `${workspaceEndpoints.deals}/${deal.id}/stage`,
    { stage, version: deal.version }
  );
  return data;
}
export async function getCrmDealActivities(id: string) {
  const { data } = await api.get<CrmActivity[]>(
    `${workspaceEndpoints.deals}/${id}/activities`
  );
  return data;
}
export async function addCrmActivity(
  dealId: string,
  payload: CrmActivityInput
) {
  const { data } = await api.post<CrmActivity>(
    `${workspaceEndpoints.deals}/${dealId}/activities`,
    payload
  );
  return data;
}
export interface CrmActivityUpdate {
  title: string;
  body?: string;
  dueAt?: string | null;
  occurredAt?: string | null;
}
export async function updateCrmActivity(
  dealId: string,
  id: string,
  payload: CrmActivityUpdate
) {
  const { data } = await api.put<CrmActivity>(
    `${workspaceEndpoints.deals}/${dealId}/activities/${id}`,
    payload
  );
  return data;
}
export async function getCrmActivities(
  params: { page?: number; kind?: string; search?: string } = {}
) {
  const { data } = await api.get<CrmPageResult<CrmActivity>>(
    workspaceEndpoints.activities,
    { params }
  );
  return data;
}
export async function completeCrmActivity(dealId: string, id: string) {
  const { data } = await api.put<CrmActivity>(
    `${workspaceEndpoints.deals}/${dealId}/activities/${id}/complete`,
    {}
  );
  return data;
}
/** Where a synced email lives, to reply inside its conversation. */
export interface CrmThreadLink {
  provider: CrmInboxProvider;
  mailboxEmail: string;
  /** Gmail thread id, also what Superhuman opens. */
  threadId: string | null;
  /** Outlook on the web link to the message. */
  webLink: string | null;
}
export async function getCrmThreadLink(
  source: 'activity' | 'support',
  id: string
) {
  const { data } = await api.post<CrmThreadLink>(
    `${workspaceEndpoints.inboxes}/thread`,
    { source, id }
  );
  return data;
}
export async function getCrmInboxes() {
  const { data } = await api.get<{ data: CrmInbox[] }>(
    workspaceEndpoints.inboxes
  );
  return data.data;
}
export async function connectCrmInbox(provider: CrmInboxProvider) {
  const { data } = await api.post<{ url: string }>(
    `${workspaceEndpoints.inboxes}/${provider}/connect`
  );
  return data;
}
export async function finishCrmInbox(code: string) {
  const { data } = await api.post(`${workspaceEndpoints.inboxes}/finish`, {
    code,
  });
  return data;
}
export async function disconnectCrmInbox(provider: CrmInboxProvider) {
  await api.delete(`${workspaceEndpoints.inboxes}/${provider}`);
}
export async function syncCrmInbox(provider: CrmInboxProvider) {
  const { data } = await api.post<{ imported: number; truncated: boolean }>(
    `${workspaceEndpoints.inboxes}/${provider}/sync`
  );
  return data;
}

export const CRM_LEAD_STATUSES = [
  'new',
  'contacted',
  'qualified',
  'disqualified',
  'converted',
] as const;
export type CrmLeadStatus = (typeof CRM_LEAD_STATUSES)[number];
export interface CrmLeadRow {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  title?: string;
  companyName?: string;
  website?: string;
  linkedinUrl?: string;
  city?: string;
  province?: string;
  country?: string;
  estimatedEmployees?: number | null;
  notes?: string;
}
export interface CrmLead extends Required<Omit<CrmLeadRow, 'email'>> {
  id: string;
  email: string | null;
  source: string;
  status: CrmLeadStatus;
  convertedDealId: string | null;
  version: number;
  createdAt: string;
}
const leadEndpoint = '/admin/crm/leads';
export async function getCrmLeads(params: {
  search?: string;
  status?: CrmLeadStatus;
  page?: number;
}) {
  const { data } = await api.get<CrmPageResult<CrmLead>>(leadEndpoint, {
    params,
  });
  return data;
}
export async function importCrmLeads(payload: {
  rows: CrmLeadRow[];
  source?: string;
  batchId?: string;
}) {
  const { data } = await api.post<{
    batchId: string;
    received: number;
    inserted: number;
    skipped: number;
  }>(`${leadEndpoint}/import`, payload);
  return data;
}
export async function setCrmLeadStatus(lead: CrmLead, status: CrmLeadStatus) {
  const { data } = await api.put<CrmLead>(`${leadEndpoint}/${lead.id}/status`, {
    status,
    version: lead.version,
  });
  return data;
}
export async function convertCrmLead(lead: CrmLead) {
  const { data } = await api.post<{ dealId: string }>(
    `${leadEndpoint}/${lead.id}/convert`
  );
  return data;
}
export async function deleteCrmLead(lead: CrmLead) {
  await api.delete(`${leadEndpoint}/${lead.id}`, {
    params: { version: lead.version },
  });
}

export async function getCrmEmailDetail(activityId: string) {
  const { data } = await api.get<{
    available: boolean;
    text: string;
    summary: string | null;
  }>(`/admin/crm/inboxes/emails/${activityId}`);
  return data;
}

export async function getCrmCountries() {
  const { data } = await api.get<LocationCountry[]>('/admin/crm/countries');
  return data;
}
export async function getCrmProvinces(countryCode: string) {
  const { data } = await api.get<LocationProvince[]>(
    `/admin/crm/countries/${countryCode}/provinces`
  );
  return data;
}

export async function getCrmStages() {
  const { data } =
    await api.get<{ stage: CrmDealStage; winProbability: number }[]>(
      '/admin/crm/stages'
    );
  return data;
}
export async function setCrmStageProbability(
  stage: CrmDealStage,
  winProbability: number
) {
  const { data } = await api.put<
    { stage: CrmDealStage; winProbability: number }[]
  >(`/admin/crm/stages/${stage}`, { winProbability });
  return data;
}

export async function createCrmLead(
  payload: CrmLeadRow & { companyName: string }
) {
  const { data } = await api.post<CrmLead>('/admin/crm/leads', payload);
  return data;
}
